/**
 * Logique PURE des demandes d'inscription (onboarding YumLink).
 * Aucune dépendance réseau/DOM → testable directement.
 * Les écritures Supabase sont dans onboardingLeadsService.ts.
 */

export type LeadStatus = 'a_valider' | 'cree' | 'rejete';

/** Une demande déposée via le formulaire yumlink.io (table `onboarding_leads`). */
export interface OnboardingLead {
  id: string;
  name: string;
  ville: string | null;
  tel: string | null;
  email: string;
  cuisine: string | null;
  lien_menu: string | null;
  slug: string | null;
  status: LeadStatus;
  tenant_id: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
  validated_at: string | null;
  validated_by: string | null;
}

export const STATUS_LABELS: Record<LeadStatus, string> = {
  a_valider: 'À valider',
  cree: 'Site créé',
  rejete: 'Rejetée',
};

/**
 * Propose un slug (adresse web) à partir du nom du resto :
 * minuscules, sans accents, lettres/chiffres/tirets, 3 à 40 caractères.
 */
export function suggestSlug(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // retire les accents (diacritiques combinants)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // tout le reste → tiret
    .replace(/^-+|-+$/g, '') // pas de tiret en bord
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/** Un slug est-il valide ? (minuscules, chiffres, tirets ; 3 à 40 car. ; pas de tiret en bord) */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/.test(slug);
}

/** Demandes en attente de validation, les plus récentes d'abord. */
export function pendingLeads(leads: OnboardingLead[]): OnboardingLead[] {
  return leads
    .filter((l) => l.status === 'a_valider')
    .slice()
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

/** Compte les demandes par statut. */
export function countByStatus(leads: OnboardingLead[]): Record<LeadStatus, number> {
  const out: Record<LeadStatus, number> = { a_valider: 0, cree: 0, rejete: 0 };
  for (const l of leads) out[l.status] = (out[l.status] ?? 0) + 1;
  return out;
}
